/* ==============================================
   Collection Template
   assets/collection-template.js
   ============================================= */

document.addEventListener('DOMContentLoaded', () => {

  // ── State ──────────────────────────────────────────────────────────────────

  const state = {
    filterCache:      [],   // [{ url, html }] — capped at 20 entries
    searchParamsInit: window.location.search.slice(1),
    searchParamsPrev: window.location.search.slice(1),
    expandedSections: [],   // filter section indexes kept open across re-renders
    currentPage:      1,    // highest page loaded so far
    totalPages:       1,    // total pages for the current filter state
    isFetchingPage:   false,// guard: prevents concurrent page fetches
    scrollObserver:   null, // IntersectionObserver for infinite scroll sentinel
    scrollController: null, // AbortController for the in-flight page scroll fetch
    filterController: null, // AbortController for in-flight filter/sort fetches
  };

  // Singleton video observer — declared here to avoid TDZ when initVideoLazyLoad
  // is called during boot (before the let declaration would otherwise be reached).
  let _videoObserver = null;


  // ── Boot ───────────────────────────────────────────────────────────────────

  setVh();
  window.addEventListener('resize', setVh, { passive: true });

  initCollectionNav();
  initDescriptionToggle();
  initProductGroups();
  initFilterPanel();
  initCheckboxes();
  initSortSelects();
  initFilterHistory();
  initVideoLazyLoad(document);

  // Pagination must be read before deciding scroll vs deep-link recovery
  readPaginationData();
  const startPage = getPageFromUrl();
  if (startPage > 1) {
    initDeepLink(startPage);
  } else {
    initInfiniteScroll();
  }


  // ── 1. Viewport height variable ────────────────────────────────────────────

  function setVh() {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  }


  // ── 2. Collection nav drag scroll + auto-center active item ───────────────

  function initCollectionNav() {
    const list = document.querySelector('.collections-list');
    if (!list) return;

    // Auto-scroll so the active pill is horizontally centered on load
    const activeItem = list.querySelector('.collection-list-item.active');
    if (activeItem) {
      const itemCenter = activeItem.offsetLeft + activeItem.offsetWidth / 2;
      list.scrollLeft  = itemCenter - window.innerWidth / 2;
    }

    // Drag scroll (desktop)
    let isDragging  = false;
    let startX      = 0;
    let startScroll = 0;

    list.addEventListener('mousedown', (e) => {
      isDragging  = true;
      startX      = e.pageX - list.offsetLeft;
      startScroll = list.scrollLeft;
      list.classList.add('active');
    });

    list.addEventListener('mouseleave', () => stopDrag());
    list.addEventListener('mouseup',    () => stopDrag());

    list.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      list.scrollLeft = startScroll - (e.pageX - list.offsetLeft - startX);
    });

    function stopDrag() {
      isDragging = false;
      list.classList.remove('active');
    }

    // Immediately move the active class to the clicked item before page load
    list.addEventListener('click', (e) => {
      const item = e.target.closest('.collection-list-item');
      if (!item || isDragging) return;
      list.querySelectorAll('.collection-list-item.active').forEach(el => el.classList.remove('active'));
      item.classList.add('active');
    });
  }


  // ── 3. Collection header description — Read more / Show less ──────────────

  function initDescriptionToggle() {
    document.addEventListener('click', (e) => {
      const labelMore = e.target.closest('.product-group-title-wrapper .product-group-content label:not(.less)');
      const labelLess = e.target.closest('.product-group-title-wrapper .product-group-content label.less');

      if (labelMore) {
        labelMore.closest('.product-group-content').classList.add('more');
      } else if (labelLess) {
        labelLess.closest('.product-group-content').classList.remove('more');
      }
    });
  }


  // ── 4. Product groups — card click filters + prev/next slider ─────────────

  function initProductGroups() {
    const sliderEl = document.getElementById('product-groups-slider');

    // Prev/next arrow buttons
    document.addEventListener('click', (e) => {
      const prevBtn = e.target.closest('.product-groups-prev-button');
      const nextBtn = e.target.closest('.product-groups-next-button');
      if (prevBtn && sliderEl) moveProductGroupSlider(-1, sliderEl);
      if (nextBtn && sliderEl) moveProductGroupSlider(1,  sliderEl);
    });

    // Arrow active state driven by scroll position
    if (sliderEl) {
      sliderEl.addEventListener('scroll', () => updateSliderArrows(sliderEl), { passive: true });
    }

    // Make each product-group card a keyboard-operable toggle button (the click
    // handler below filters on .product-group-container). aria-pressed tracks the
    // .active class so it can't drift no matter which path toggles it.
    document.querySelectorAll('.product-group-container').forEach((card) => {
      if (card.__a11yGroup) return;
      card.__a11yGroup = true;
      if (!card.hasAttribute('role')) card.setAttribute('role', 'button');
      if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '0');
      const title = card.querySelector('.product-group-title');
      if (title && !card.hasAttribute('aria-label')) {
        card.setAttribute('aria-label', 'Filter by ' + title.textContent.trim());
      }
      const syncPressed = () => card.setAttribute('aria-pressed', card.classList.contains('active') ? 'true' : 'false');
      new MutationObserver(syncPressed).observe(card, { attributes: true, attributeFilter: ['class'] });
      syncPressed();
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          card.click();
        }
      });
    });

    // Card click → activate matching style + weight filter checkboxes
    document.addEventListener('click', (e) => {
      const card = e.target.closest('.product-group-container');
      if (!card) return;

      // Deselect all style + weight checkboxes
      document.querySelectorAll('.checkbox.style-checkbox.active, .checkbox.weight-checkbox.active')
        .forEach(el => el.classList.remove('active'));

      if (card.classList.contains('active')) {
        // Toggle off — deactivate this card and show everything
        card.classList.remove('active');
      } else {
        // Deactivate any other active card
        document.querySelectorAll('.product-group-container.active')
          .forEach(el => el.classList.remove('active'));

        // Activate matching style checkboxes
        (card.dataset.val || '').split(',').forEach(key => {
          const cb = document.querySelector(`.checkbox.style-checkbox[data-val="${key}"]`);
          if (cb) cb.classList.add('active');
        });

        // Activate matching weight checkboxes
        (card.dataset.weights || '').split(',').forEach(key => {
          if (!key) return;
          const cb = document.querySelector(`.checkbox.weight-checkbox[data-val="${key}"]`);
          if (cb) cb.classList.add('active');
        });

        card.classList.add('active');
      }

      submitFilters();

      // Smooth-scroll to the grid, offset by the sticky nav bar
      const filtersRow = document.querySelector('.filters-row');
      const navBar     = document.querySelector('.collections-list-wrapper');
      if (filtersRow) {
        const targetY = filtersRow.getBoundingClientRect().top
          + window.scrollY
          - (navBar ? navBar.offsetHeight : 0);
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    });
  }

  function moveProductGroupSlider(direction, sliderEl) {
    sliderEl.scrollBy({ left: direction * sliderEl.clientWidth, behavior: 'smooth' });
  }

  function updateSliderArrows(sliderEl) {
    const prevBtn = document.querySelector('.product-groups-prev-button');
    const nextBtn = document.querySelector('.product-groups-next-button');
    if (!prevBtn || !nextBtn) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderEl;
    prevBtn.classList.toggle('active', scrollLeft > 0);
    nextBtn.classList.toggle('active', Math.ceil(scrollLeft + clientWidth) < scrollWidth);
  }


  // ── 5. Filter panel — open / close / body lock ────────────────────────────

  function initFilterPanel() {
    const panel  = document.querySelector('.filters-dropdown-container');
    const header = document.getElementById('shopify-section-header');
    if (!panel) return;

    const dialog  = panel.querySelector('.filters-section-wrapper');
    const trigger = document.getElementById('filters-button-container');
    let lastFocus = null;
    let keydownHandler = null;

    document.addEventListener('click', (e) => {
      if      (e.target.closest('#filters-button-container'))       openPanel();
      else if (e.target.closest('.filters-close-button'))           closePanel();
      else if (e.target.closest('.filters-dropdown-apply-button'))  closePanel();
      else if (e.target.closest('.filters-dropdown-overlay'))       closePanel();
    });

    // Keyboard activation for the click-driven filter controls. Delegated on
    // document so it keeps working after the panel re-renders its inner HTML.
    const ACTIVATE_SELECTOR = '#filters-button-container, .filters-close-button, ' +
      '.sort-select, .filters-dropdown-reset-button, .filters-dropdown-apply-button, ' +
      '.filters-dropdown-section .checkbox:not(.color-group-checkbox)';
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const el = e.target.closest(ACTIVATE_SELECTOR);
      if (!el) return;
      e.preventDefault();
      if (el.getAttribute('aria-disabled') === 'true') return;
      el.click();
    });

    function focusables() {
      if (!dialog) return [];
      return Array.prototype.filter.call(
        dialog.querySelectorAll('a[href], button, [role="button"], [role="checkbox"], input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'),
        (el) => el.offsetParent !== null && el.getAttribute('aria-disabled') !== 'true'
      );
    }

    function openPanel() {
      lastFocus = document.activeElement;
      panel.classList.add('active');
      document.body.classList.add('overflow-hidden');
      if (header) header.classList.add('shopify-section-header-hidden');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Esc closes; Tab is trapped within the dialog.
      keydownHandler = (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closePanel(); return; }
        if (e.key === 'Tab') {
          const f = focusables();
          if (!f.length) return;
          const first = f[0], last = f[f.length - 1];
          if (!dialog.contains(document.activeElement)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
          else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', keydownHandler, true);

      if (dialog) {
        if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
        setTimeout(() => dialog.focus(), 0);
      }
    }

    function closePanel() {
      panel.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (header) header.classList.remove('shopify-section-header-hidden');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (keydownHandler) { document.removeEventListener('keydown', keydownHandler, true); keydownHandler = null; }
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }
  }


  // ── 6. Checkboxes — toggle active state + submit ──────────────────────────

  function initCheckboxes() {
    // Checkbox click
    document.addEventListener('click', (e) => {
      const cb = e.target.closest('.checkbox');
      if (!cb || cb.classList.contains('disabled')) return;

      cb.classList.toggle('active');

      // If a style or weight checkbox is changed manually, deactivate product group card
      if (cb.classList.contains('style-checkbox') || cb.classList.contains('weight-checkbox')) {
        document.querySelectorAll('.product-group-container.active')
          .forEach(el => el.classList.remove('active'));
      }

      // Snapshot which filter sections are open before re-render
      state.expandedSections = [];
      document.querySelectorAll('.filters-dropdown-section').forEach((section, i) => {
        if (section.classList.contains('expanded')) state.expandedSections.push(i);
      });

      showFilterLoader();
      submitFilters();
    });

    // Reset All button
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.filters-dropdown-reset-button')) return;

      document.querySelectorAll('.checkbox.active').forEach(el => el.classList.remove('active'));
      document.querySelectorAll('.product-group-container.active').forEach(el => el.classList.remove('active'));

      const colorItem   = document.querySelector('.selected-filter-item[data-type="color"]');
      const tagToRemove = colorItem ? colorItem.dataset.val : null;

      showFilterLoader();
      submitFilters(tagToRemove);
    });
  }


  // ── 7. Sort selects ───────────────────────────────────────────────────────

  function initSortSelects() {
    document.addEventListener('click', (e) => {
      const sortEl = e.target.closest('.sort-select');
      if (!sortEl) return;

      e.stopPropagation();
      if (sortEl.classList.contains('active')) return;

      showFilterLoader();
      submitFilters(null, sortEl.dataset.sortBy);
    });
  }


  // ── 8. Infinite scroll — fetch + append pages on sentinel intersection ─────

  function readPaginationData() {
    const grid = document.getElementById('product-grid');
    state.totalPages = parseInt(grid?.dataset.totalPages || '1', 10);
  }

  function getPageFromUrl() {
    return parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
  }

  function initInfiniteScroll() {
    const sentinel = document.querySelector('.grid-sentinel');
    if (!sentinel) return;

    // Abort any in-flight page scroll fetch and reset the guard flag.
    // Without this, a fetch started by the previous filter's observer would
    // keep isFetchingPage=true, blocking the new observer's first fire and
    // making it appear that page 2 never loads after applying a second filter.
    if (state.scrollController) {
      state.scrollController.abort();
      state.scrollController = null;
    }
    state.isFetchingPage = false;

    // Disconnect any existing observer before creating a new one
    if (state.scrollObserver) {
      state.scrollObserver.disconnect();
      state.scrollObserver = null;
    }

    sentinel.classList.remove('loading', 'all-loaded');

    if (state.currentPage >= state.totalPages) {
      sentinel.classList.add('all-loaded');
      return;
    }

    state.scrollObserver = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting || state.isFetchingPage) return;
      if (state.currentPage >= state.totalPages) {
        state.scrollObserver.disconnect();
        sentinel.classList.add('all-loaded');
        return;
      }

      state.isFetchingPage = true;
      sentinel.classList.add('loading');

      const nextPage       = state.currentPage + 1;
      state.scrollController = new AbortController();
      const html           = await fetchPageHtml(buildPageFetchUrl(nextPage), state.scrollController.signal);
      state.scrollController = null;
      const appended       = html ? appendPageToGrid(html) : false;

      // Only advance state if products were actually inserted into the DOM.
      // If the fetch succeeded but the page had no visible products (all
      // filtered out by Liquid conditions), appended is false and we still
      // increment currentPage so the observer moves on rather than refetching
      // the same empty page forever.
      if (appended) {
        initVideoLazyLoad(document.getElementById('product-grid'));
      }
      if (html) {
        // Advance even on empty pages so we don't loop on the same page.
        state.currentPage = nextPage;
        updateUrlWithPage(nextPage);
      }

      sentinel.classList.remove('loading');
      state.isFetchingPage = false;

      if (state.currentPage >= state.totalPages) {
        state.scrollObserver.disconnect();
        sentinel.classList.add('all-loaded');
      }
    // 1200px look-ahead: start fetching the next page well before the user
    // reaches the sentinel, so new products appear before they're needed.
    }, { rootMargin: '0px 0px 1200px 0px' });

    state.scrollObserver.observe(sentinel);
  }

  // Deep link recovery: user loaded ?page=N directly.
  // Fetch pages 1→N, replace server-rendered content with the full sequence.
  async function initDeepLink(targetPage) {
    const sentinel   = document.querySelector('.grid-sentinel');
    const targetGrid = document.querySelector('#product-grid .grid');

    if (sentinel)   sentinel.classList.add('loading');
    if (targetGrid) targetGrid.innerHTML = '';

    for (let p = 1; p <= targetPage; p++) {
      const html = await fetchPageHtml(buildPageFetchUrl(p));
      if (html) appendPageToGrid(html);
    }

    state.currentPage = targetPage;
    if (sentinel) sentinel.classList.remove('loading');

    initInfiniteScroll();
    initVideoLazyLoad(document.getElementById('product-grid'));
  }

  // Build the fetch URL for a specific page, preserving current filter/sort params.
  function buildPageFetchUrl(page) {
    const sectionId = getSectionId();
    const params    = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('section_id', sectionId);
    if (page > 1) params.set('page', page);
    return `${window.location.pathname}?${params.toString()}`;
  }

  async function fetchPageHtml(url, signal = null) {
    const cached = state.filterCache.find(e => e.url === url);
    if (cached) return cached.html;

    try {
      const res  = await fetch(url, signal ? { signal } : undefined);
      const html = await res.text();
      // Don't cache aborted responses
      state.filterCache.push({ url, html });
      if (state.filterCache.length > 20) state.filterCache.shift();
      return html;
    } catch (e) {
      // AbortError is expected when a filter change cancels an in-flight fetch
      return null;
    }
  }

  // Returns true if at least one item was appended, false otherwise.
  // Callers must check the return value before advancing state.currentPage or
  // updating the URL — otherwise the page counter increments even when nothing
  // was added (e.g. sourceGrid not found, or the page had no visible products).
  function appendPageToGrid(html) {
    const parsed     = new DOMParser().parseFromString(html, 'text/html');
    const sourceGrid = parsed.querySelector('#product-grid .grid');
    const targetGrid = document.querySelector('#product-grid .grid');
    if (!sourceGrid || !targetGrid) return false;

    const items = sourceGrid.innerHTML.trim();
    if (!items) return false;

    targetGrid.insertAdjacentHTML('beforeend', items);
    return true;
  }

  function updateUrlWithPage(page) {
    const params = new URLSearchParams(window.location.search);
    if (page <= 1) {
      params.delete('page');
    } else {
      params.set('page', page);
    }
    const search = params.toString();
    history.replaceState(
      { searchParams: search, page },
      '',
      window.location.pathname + (search ? '?' + search : '')
    );
  }


  // ── 9. Video lazy load — single persistent observer ────────────────────────
  // One observer instance is reused; new videos are added to it as pages load.

  function initVideoLazyLoad(scope) {
    if (!_videoObserver) {
      _videoObserver = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          loadAndPlayVideo(entry.target);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px 300px 0px' });
    }

    scope.querySelectorAll('video[data-lazy]').forEach(v => _videoObserver.observe(v));
  }

  function loadAndPlayVideo(video) {
    // Copy data-src → src on each <source>
    Array.from(video.children).forEach(source => {
      if (source.tagName === 'SOURCE' && source.dataset.src) {
        source.src = source.dataset.src;
        delete source.dataset.src;
      }
    });

    video.load();
    video.currentTime = 0.1; // force a keyframe on iOS before play() resolves

    video.play()
      .then(() => applyVideoPlayedState(video))
      .catch(() => {
        // Autoplay blocked — retry on first user interaction
        const retry = () => video.play().then(() => applyVideoPlayedState(video));
        document.body.addEventListener('mousedown', retry, { once: true, passive: true });
        document.body.addEventListener('touchstart', retry, { once: true, passive: true });
      });

    video.removeAttribute('data-lazy');
  }

  function applyVideoPlayedState(video) {
    if (video.classList.contains('element-item-model-video')) {
      // Model hover video
      video.classList.add('video-playing');
    } else {
      // Poster video — hide placeholder, show video
      const poster = video.closest('.element-item-poster');
      if (poster) poster.classList.add('video-played');
    }
  }


  // ── 10. AJAX filter + sort system ─────────────────────────────────────────

  function initFilterHistory() {
    window.addEventListener('popstate', (e) => {
      // popstate fires on back/forward — only for filter pushState events,
      // since page changes use replaceState and don't create history entries.
      const params = e.state?.searchParams ?? state.searchParamsInit;
      if (params === state.searchParamsPrev) return;
      renderPage(params, false);
    });
  }

  function submitFilters(tagToRemove = null, sortBy = null) {
    const styles      = [];
    const weights     = [];
    const sizes       = [];
    const collections = [];

    document.querySelectorAll('.checkbox.active').forEach(cb => {
      const val = cb.dataset.val;
      if      (cb.classList.contains('style-checkbox'))      styles.push(val);
      else if (cb.classList.contains('weight-checkbox'))     weights.push(val);
      else if (cb.classList.contains('size-checkbox'))       sizes.push(val);
      else if (cb.classList.contains('collection-checkbox')) collections.push(val);
    });

    // Build query using URLSearchParams so multiple values for the same filter
    // are emitted as separate params (filter.v.option.size=XL&filter.v.option.size=M)
    // rather than comma-joined. A comma in a raw query string survives Shopify's
    // parser, but URLSearchParams.toString() percent-encodes commas to %2C which
    // Shopify does not treat as a value separator — causing empty results on
    // page 2+ when buildPageFetchUrl reconstructs the URL via URLSearchParams.
    const params = new URLSearchParams();
    params.append('filter.v.availability', '1');
    sizes.forEach(v       => params.append('filter.v.option.size', v));
    weights.forEach(v     => params.append('filter.p.m.custom.weight_v', v));
    styles.forEach(v      => params.append('filter.p.m.custom.style_v', v));
    collections.forEach(v => params.append('filter.p.m.custom.collection_v', v));
    if (sortBy)             params.append('sort_by', sortBy);

    renderPage(params.toString(), true, tagToRemove);
  }

  function renderPage(searchParams, updateHistory = true, tagToRemove = null) {
    state.searchParamsPrev = searchParams;

    const sectionId = getSectionId();
    if (!sectionId) return;

    // Strip colour tag from pathname if a colour filter is being reset
    const pathname = tagToRemove
      ? window.location.pathname.replace('/' + tagToRemove, '')
      : window.location.pathname;

    // Filter fetches always load page 1 (no &page= in searchParams)
    const url = `${pathname}?section_id=${sectionId}&${searchParams}`;

    if (updateHistory) {
      history.pushState({ searchParams }, '', `${pathname}${searchParams ? '?' + searchParams : ''}`);
    }

    // Cancel any in-flight filter request before starting a new one
    if (state.filterController) state.filterController.abort();
    state.filterController = new AbortController();

    const cached = state.filterCache.find(entry => entry.url === url);
    if (cached) {
      applyRenderedHTML(cached.html);
      return;
    }

    fetch(url, { signal: state.filterController.signal })
      .then(res => res.text())
      .then(html => {
        state.filterCache.push({ url, html });
        if (state.filterCache.length > 20) state.filterCache.shift();
        applyRenderedHTML(html);
        state.filterController = null;
      })
      .catch(err => {
        if (err.name !== 'AbortError') console.error('Filter fetch failed:', err);
      });
  }

  // Remember which filter control is focused before a re-render, so we can
  // restore focus afterwards (replaceInnerHTML destroys the focused element).
  function captureFilterFocusSelector() {
    const a = document.activeElement;
    if (!a || !a.closest || !a.closest('.filters-dropdown-container')) return null;
    const esc = (v) => (window.CSS && CSS.escape) ? CSS.escape(v) : v;
    if (a.classList.contains('sort-select')) {
      return '.sort-select[data-sort-by="' + esc(a.dataset.sortBy || '') + '"]';
    }
    if (a.classList.contains('checkbox')) {
      const sub = ['size-checkbox', 'style-checkbox', 'weight-checkbox']
        .find((c) => a.classList.contains(c)) || 'checkbox';
      return '.' + sub + '[data-val="' + esc(a.dataset.val || '') + '"]';
    }
    return null;
  }

  function applyRenderedHTML(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const focusSelector = captureFilterFocusSelector();

    // Read new pagination state from the parsed response BEFORE replacing the DOM,
    // because replaceInnerHTML only swaps innerHTML — not the element's own attributes.
    const newGrid = parsed.getElementById('product-grid');
    if (newGrid) {
      state.totalPages  = parseInt(newGrid.dataset.totalPages || '1', 10);
      state.currentPage = 1;
    }

    // Replace product grid inner content
    replaceInnerHTML('product-grid', parsed);

    // Sync pagination data-attributes on the live element
    const liveGrid = document.getElementById('product-grid');
    if (liveGrid && newGrid) {
      liveGrid.dataset.totalPages  = newGrid.dataset.totalPages  || '1';
      liveGrid.dataset.currentPage = '1';
    }

    // Replace filter UI elements
    replaceInnerHTML('collection-product-count', parsed);
    replaceInnerHTML('filters-section',          parsed);
    replaceInnerHTML('sort-items',               parsed);
    replaceInnerHTML('filters-dropdown-apply-button', parsed);
    replaceInnerHTML('filters-button-container', parsed);

    // Re-observe videos in the new grid content
    const grid = document.getElementById('product-grid');
    if (grid) initVideoLazyLoad(grid);

    // Re-attach scroll observer for the new page state
    initInfiniteScroll();

    // Hide filter loading overlay
    hideFilterLoader();

    // Restore previously expanded filter sections
    document.querySelectorAll('.filters-dropdown-section').forEach((section, i) => {
      if (state.expandedSections.includes(i)) section.classList.add('expanded');
    });
    state.expandedSections = [];

    // Restore keyboard focus to the control that triggered the re-render.
    if (focusSelector) {
      const el = document.querySelector(focusSelector);
      if (el) el.focus();
    }
  }

  function replaceInnerHTML(id, parsedDoc) {
    const live   = document.getElementById(id);
    const source = parsedDoc.getElementById(id);
    if (live && source) live.innerHTML = source.innerHTML;
  }

  function getSectionId() {
    const el = document.getElementById('collection-template');
    return el ? el.dataset.id : null;
  }


  // ── Helpers ────────────────────────────────────────────────────────────────

  function showFilterLoader() {
    const loader = document.querySelector('.filters-dropdown-sections-container .filters-loader');
    if (loader) loader.classList.add('active');
  }

  function hideFilterLoader() {
    const loader = document.querySelector('.filters-dropdown-sections-container .filters-loader');
    if (loader) loader.classList.remove('active');
  }

});
